import { tradePromoOptimizationUrls } from 'config/urls';
import { UserContext } from 'contexts/ConfigContext';
import { TPODataContext } from 'contexts/TPOContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    formatNumberWithCommas,
    generateErrorMsg,
    getConfig,
    removePropertyByKeyName,
    sendNotification,
    transformFiltersV2,
} from 'utils/helperMethods';
import _ from 'lodash';
import InfiniteScroller from '../InfiniteScroller';
import { CustomTable } from 'components/template/PricingSimulator/PricingSimulatorIndexTableWrapper';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import { useFetchToolsLibraryData } from 'hooks/tradePromoOptimization/plans/useFetchToolsLibraryData';
import { getPromoLibraryTableColumns } from 'utils/getTradePromoOptimizationTableColumns';
import Footer from '../PricingSimulator/Footer';
import KPISelector from './AddEventModal/PromoSections/KPISelector';
import { Button } from 'antd';
import { ProjectOutlined } from '@ant-design/icons';
import { PromoLibraryKpiConfigType } from 'types/tradePromoOptimization';
import TableErrorBox from '../ErrorBox/TableErrorBox';
import EditToolsMetricsModal from './Modals/EditToolsMetricsModal';
import { initialEditModalState } from './PromoLibraryContainer';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { queryClient } from 'services/reactQueryService';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';
import { transformFilterObjectWithDimensionsInfo } from 'utils/getPricingSimulatorTableColumns';
import { ToolTabKeys } from 'components/organism/TradePromoOptimization/Tools';
import NewGlobalFilter from '../common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from '../PricingSimulator/FilterRibbon/FilterRibbon';
import { Download } from 'assets/svg';

const mechanicsLibraryTableId = 'mechanics-library-table-id';

const MechanicsLibraryContainer = () => {
    const {
        appConfig,
        exportPromoLibraryOptions: { exportToolsData },
    } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const promoLibraryExportThreshold = tpoConfig?.promoLibraryExportThreshold;

    const {
        availableFilters,
        allCheckedAttribute,
        setAllCheckedAttribute,
        allSelectedValueForAllAttribute,
        setAllSelectedValueForAllAttribute,
        setApplyFilterObject,
        applyFilterObject,
        resetFilterObject,
        uiDimensionConfigData,
        filterUIDimensionsLoading,
    } = useContext(TPODataContext);
    const [mechanicsLibraryTableVariable, setMechanicsLibraryTableVariable] = useState<any>({
        limit: tpoConfig?.tableLimit,
        offset: 0,
        kpis: [],
        library_group: ToolTabKeys.MechanicsLibrary,
        filters: {},
    });
    const mechanicsLibraryAttributeTypes = tpoConfig?.mechanicsLibraryAttributeTypes;
    const [mechanicLibraryKpiConfigData, setMechanicLibraryKpiConfigData] = useState<PromoLibraryKpiConfigType | null>(
        null,
    );
    const [openEditModal, setOpenEditModal] = useState({ ...initialEditModalState });

    const [isKpiFilterVisible, setIsKpiFilterVisible] = useState(false);
    const [kpiFilterDropdownValues, setKpiDropdownValues] = useState<any[]>([]);
    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});

    const tableHeight = useRef<number | null>(0);

    //this hook with give height to fill the screen height
    tableHeight.current = useFillHeightToFitScreen({
        id: mechanicsLibraryTableId,
        height: tableHeight.current,
        buffer: 110, //footer
        //triggerDummyRender: true,
    });

    //GET  MECHANICS LIBRARY KPI CONFIG DATA FROM BACKEND
    const { loading: mechanicLibraryKPIConfigLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: {},
        queryKey: ['tpo-mechanic-library-kpi-config'],
        url: `${tradePromoOptimizationUrls.getPromoLibraryKpiUrl}/${ToolTabKeys.MechanicsLibrary}`,
        variables: {},
        parserFunction(data: any) {
            const res: PromoLibraryKpiConfigType = {};
            data?.forEach((item: any) => {
                res[item?.kpi_key] = {
                    pre: item?.pre,
                    post: item?.post,
                    label: item?.kpi_name,
                    allowNegative: item?.allow_negative,
                    isEditable: item?.is_editable,
                    maxValue: item?.max_value,
                    minValue: item?.min_value,
                    type: item?.kpi_type,
                    storageType: item?.storage_type,
                    allowedDecimals: item?.allowed_decimals,
                    allowedValues: item?.allowed_values,
                };
            });
            setMechanicLibraryKpiConfigData(res);
            return {};
        },
    });
    //mechanics library data
    const {
        loading: mechanicsLibraryTableDataLoading,
        error: mechanicsLibraryTableDataError,
        data: mechanicsLibraryTableData,
        count: mechanicsLibraryTableDataCount,
    } = useFetchToolsLibraryData({
        method: 'post',
        queryConfig: {
            enabled: Boolean(
                mechanicsLibraryTableVariable?.kpis?.length > 0 &&
                    Object.keys(mechanicsLibraryTableVariable?.filters)?.length > 0 &&
                    uiDimensionConfigData?.filterKeys?.length > 0,
            ),
        },
        url: tradePromoOptimizationUrls.getPromoLibraryTableDataUrl,
        queryKey: ['tpo-promo-library-data', 'tpo-mechanics-library-table-data', mechanicsLibraryTableVariable],
        variables: { ...(mechanicsLibraryTableVariable || {}) },
        kpiConfigData: mechanicLibraryKpiConfigData,
        attributeTypes: mechanicsLibraryAttributeTypes,
    });
    const commonLoading =
        filterUIDimensionsLoading || mechanicLibraryKPIConfigLoading || mechanicsLibraryTableDataLoading;

    const { mutate: updateMechanicsLibrary, loading: updateMechanicsLibraryLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.updatePromoLibraryUrl,
        method: 'post',
    });

    function onEdit(
        from: 'cell' | 'column',
        kpiObject: PromoLibraryKpiConfigType,
        value?: any,
        rowId?: number,
        rowLabel?: string,
    ) {
        setOpenEditModal({
            kpiData: {
                kpiObject,
                value: from === 'column' ? undefined : value,
                rowId: from === 'column' ? undefined : rowId,
                rowLabel: from === 'column' ? undefined : rowLabel,
            },
            open: true,
        });
    }

    const columns = useMemo(() => {
        if (!mechanicsLibraryTableData) return [];
        const tempColumns = kpiFilterDropdownValues
            ?.filter((item) => item.isSelected)
            ?.map((item) => {
                return {
                    label: item?.label,
                    key: item?.label,
                    kpiInfo: item,
                    sortKey: item?.storageType === 'range' ? false : item?.value,
                };
            });
        const memoisedColumns = getPromoLibraryTableColumns(mechanicsLibraryAttributeTypes, tempColumns, onEdit, {
            sortBy: mechanicsLibraryTableVariable?.sort_by,
            sortOrder: mechanicsLibraryTableVariable?.sort_order === 'asc' ? 'ascend' : 'descend',
        });
        return memoisedColumns;
    }, [mechanicsLibraryTableData]);

    const isErrorStatus = mechanicsLibraryTableDataError;
    const footerFirstText = `${
        'Showing ' +
        Number(mechanicsLibraryTableData?.length ?? 0)?.toLocaleString() +
        ' of ' +
        formatNumberWithCommas(mechanicsLibraryTableDataCount, 0) +
        ' records'
    }`;
    const footerSecondText = '';
    const footerThirdText = '';
    const footerFourthText = '';

    const onFilterSaveClickHandler = (resultObj: any) => {
        setAllCheckedAttribute(resultObj.allCheckedAttribute);
        setAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
        setApplyFilterObject(resultObj.applyFilterObject);
    };

    const commonVariables = useMemo(() => {
        if (selectedFilter?.value) {
            const updatedFilters = transformFiltersV2(
                tempApplyFilterObject,
                uiDimensionConfigData.filterKeys,
                selectedFilter.value,
            );
            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
                library_group: ToolTabKeys.MechanicsLibrary,
            };
        }
    }, [selectedFilter]);

    useEffect(() => {
        const updatedFilters = transformFiltersV2(applyFilterObject, uiDimensionConfigData?.filterKeys ?? []);
        setMechanicsLibraryTableVariable((prev: any) => ({ ...prev, filters: updatedFilters, offset: 0 }));
    }, [applyFilterObject]);

    useEffect(() => {
        const valueList: any[] = [];
        const updatedList = Object.keys(mechanicLibraryKpiConfigData ?? {})?.map((configKey) => {
            valueList.push({
                key: configKey,
                label: mechanicLibraryKpiConfigData?.[configKey]['label'],
                storage_type: mechanicLibraryKpiConfigData?.[configKey]['storageType'],
            });
            return {
                ...mechanicLibraryKpiConfigData?.[configKey],
                label: mechanicLibraryKpiConfigData?.[configKey]['label'],
                value: configKey,
                isSelected: true,
                pre: mechanicLibraryKpiConfigData?.[configKey]['pre'],
                post: mechanicLibraryKpiConfigData?.[configKey]['post'],
            };
        });
        setMechanicsLibraryTableVariable((prev: any) => {
            return {
                ...prev,
                offset: 0,
                kpis: valueList,
                sort_by: mechanicsLibraryAttributeTypes?.[0]?.sortKey,
                sort_order: 'asc',
            };
        });
        setKpiDropdownValues(updatedList);
    }, [mechanicLibraryKpiConfigData]);

    function infiniteScrollOnNext(loading: boolean) {
        if (loading) return;
        const newOffset = mechanicsLibraryTableVariable.offset + mechanicsLibraryTableVariable.limit;
        setMechanicsLibraryTableVariable({ ...mechanicsLibraryTableVariable, offset: newOffset });
    }

    function exportMechanicsLibrary() {
        if (mechanicsLibraryTableDataCount > promoLibraryExportThreshold) {
            return sendNotification(
                'error',
                `The data exceeds the export limit of ${formatNumberWithCommas(
                    promoLibraryExportThreshold,
                    0,
                )} records. Please apply more filters to reduce the data before proceeding with the export.
            `,
                10,
                500,
            );
        }
        sendNotification('info', tpoNotificationMessages.PLAN_EXPORT_QUEUE, 7, 500);
        const variables = {
            ...mechanicsLibraryTableVariable,
            limit: isNaN(mechanicsLibraryTableDataCount) ? promoLibraryExportThreshold : mechanicsLibraryTableDataCount,
            default_headers: mechanicsLibraryAttributeTypes?.map((item: any) => ({
                key: item?.key,
                label: item?.label,
            })),
        };
        exportToolsData(variables, `mechanics_library.csv`);
    }

    return (
        <div className="relative">
            <div className="flex justify-between mt-12 mb-2 items-center">
                <div className="flex items-center gap-2"></div>

                <div className="flex justify-end gap-2 items-center">
                    {/* <Legend className="legend-size-square" data={legendItems} /> */}
                    <NewGlobalFilter
                        availableFilters={availableFilters}
                        allCheckedAttribute={allCheckedAttribute}
                        allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                        applyFilterObject={applyFilterObject}
                        resetFilterObject={resetFilterObject}
                        onSubmitHandler={onFilterSaveClickHandler}
                        url={tradePromoOptimizationUrls.getAttributeNames}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        tempApplyFilterObject={tempApplyFilterObject}
                        setTempApplyFilterObject={setTempApplyFilterObject}
                        apiVariables={commonVariables}
                        filterDimensionInfo={uiDimensionConfigData?.filterDimensionInfo}
                        showAttributePanelToLeft
                    />
                    <KPISelector
                        buttonComponent={
                            <Button
                                onClick={() => {
                                    setIsKpiFilterVisible(!isKpiFilterVisible);
                                }}
                                icon={<ProjectOutlined style={{ position: 'relative', top: -3 }} />}
                                style={{ borderRadius: 4, margin: 0 }}
                                type="default"
                            >
                                KPI
                            </Button>
                        }
                        isVisible={isKpiFilterVisible}
                        setIsVisible={setIsKpiFilterVisible}
                        kpiList={kpiFilterDropdownValues}
                        onSubmit={(values) => {
                            const updatedList = kpiFilterDropdownValues?.map((item) => {
                                return {
                                    ...item,
                                    isSelected: values.includes(item?.value),
                                };
                            });
                            const updatedValues = kpiFilterDropdownValues
                                ?.filter((kpiItem) => {
                                    return values.includes(kpiItem?.value);
                                })
                                .map((item) => ({ key: item?.value, storage_type: item?.storageType }));

                            setMechanicsLibraryTableVariable({
                                ...mechanicsLibraryTableVariable,
                                offset: 0,
                                kpis: updatedValues,
                                sort_by:
                                    updatedValues?.find((item) => item?.key === mechanicsLibraryTableVariable?.sort_by)
                                        ?.key ?? mechanicsLibraryAttributeTypes?.[0]?.sortKey,
                                sort_order: mechanicsLibraryTableVariable?.sort_order ?? 'asc',
                            });
                            setKpiDropdownValues(updatedList);
                        }}
                        variableName={'KPI'}
                        hideSearch={true}
                    />
                    {mechanicsLibraryTableData?.length > 0 && (
                        <span
                            className="cursor-pointer"
                            onClick={() => {
                                exportMechanicsLibrary();
                            }}
                        >
                            <Download />
                        </span>
                    )}
                </div>
            </div>
            {/*3.  Filter Ribbon  */}
            <div className="my-3">
                <FilterRibbon
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    resetFilterObject={resetFilterObject}
                    onSubmitHandler={(obj: any) => {
                        setAllCheckedAttribute(obj.allCheckedAttribute);
                        setAllSelectedValueForAllAttribute(obj.allSelectedValueForAllAttribute);
                        setApplyFilterObject(obj.applyFilterObject);
                    }}
                />
            </div>

            <InfiniteScroller
                data={mechanicsLibraryTableData}
                loading={commonLoading}
                hasMore={mechanicsLibraryTableData?.length < (mechanicsLibraryTableDataCount || 0) || commonLoading}
                height={
                    mechanicsLibraryTableData?.length && !mechanicsLibraryTableDataError
                        ? tableHeight.current ?? undefined
                        : isNaN(Number(tableHeight.current))
                        ? undefined
                        : Number(tableHeight.current) - 1
                }
                //height={200}
                //     loader={true}
                onNext={() => infiniteScrollOnNext(commonLoading)}
            >
                <CustomTable
                    bordered
                    onChange={(pagination, filter, sort: any, extra) => {
                        if (sort?.order) {
                            const sortMapping: any = {
                                descend: 'desc',
                                ascend: 'asc',
                            };

                            setMechanicsLibraryTableVariable({
                                ...mechanicsLibraryTableVariable,
                                sort_by: sort?.column?.sortKey,
                                sort_order: sortMapping?.[sort?.order] || 'asc',
                                offset: 0,
                            });
                        }
                    }}
                    sticky={{
                        getContainer: () =>
                            document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                    }} //adding x axis scrollbar on table
                    id={mechanicsLibraryTableId}
                    columns={columns}
                    // loading={commonLoading}
                    scroll={{ x: '100%' }}
                    dataSource={mechanicsLibraryTableData}
                    pagination={false}
                    showSorterTooltip={false}
                    style={{
                        opacity: commonLoading ? '0.3' : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: commonLoading ? (
                            <></>
                        ) : mechanicsLibraryTableDataError ? (
                            <TableErrorBox error={mechanicsLibraryTableDataError} height={tableHeight.current} />
                        ) : (
                            ''
                        ),
                    }}
                />
            </InfiniteScroller>
            <Footer
                type={isErrorStatus ? 'error' : 'default'}
                items={[footerFirstText, footerSecondText, footerThirdText, footerFourthText]}
            />
            <EditToolsMetricsModal
                updatePromoLibraryLoading={updateMechanicsLibraryLoading}
                data={openEditModal.kpiData}
                open={openEditModal.open}
                onSubmit={(payload: any) => {
                    const updatedPayload = {
                        ...(payload ?? {}),
                        library_group: ToolTabKeys.MechanicsLibrary,
                        filters: mechanicsLibraryTableVariable.filters,
                    };
                    //first clear the variables so that on success fresh api can be triggered
                    updateMechanicsLibrary(updatedPayload, {
                        onSuccess: (data: any) => {
                            //on success, REMOVE THE CACHE
                            setMechanicsLibraryTableVariable({ ...mechanicsLibraryTableVariable, offset: 0 });
                            sendNotification('success', tpoNotificationMessages.EDIT_PROMO_LIBRARY_SUCCESS);

                            queryClient.removeQueries({
                                exact: false,
                                queryKey: ['tpo-promo-library-data', 'tpo-mechanics-library-table-data'],
                            });
                            setOpenEditModal({ ...initialEditModalState });
                        },
                        onError: (error: any) => {
                            const errorMsg = generateErrorMsg(
                                String(error?.response?.data?.detail || ''),
                                ErrorMessages.GENERALIZED_ERROR_MSG,
                            );
                            //show error message
                            sendNotification('error', errorMsg);
                            setOpenEditModal({ ...initialEditModalState });
                        },
                    });
                }}
                onCancel={() => {
                    setOpenEditModal({ ...initialEditModalState });
                }}
            />
        </div>
    );
};

export default MechanicsLibraryContainer;
